import vehiclesData from '../data/vehicles.json';
import thresholdsData from '../data/thresholds.json';
import { getTemperatureStatus } from '../utils/temperatureUtils';

/**
 * Generate realistic temperature reading with natural fluctuations
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @param {number} baseTemp - Base temperature (default optimal)
 * @param {Date} timestamp - Timestamp for the reading
 * @returns {object} - Temperature reading object
 */
export const generateTemperatureReading = (vehicleId, baseTemp = 5, timestamp = new Date()) => {
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  
  // Calculate optimal mid-point if baseTemp not provided
  if (baseTemp === 5) {
    baseTemp = (thresholds.optimal_min + thresholds.optimal_max) / 2;
  }
  
  // Add natural variation (±0.5°C) with some drift
  const variation = (Math.random() - 0.5) * 1;
  
  // Add time-based cycle (cooler at night)
  const hour = timestamp.getHours();
  const timeEffect = Math.sin((hour - 6) * Math.PI / 12) * 0.5;
  
  // Add occasional anomalies (5% chance)
  const anomaly = Math.random() < 0.05 ? (Math.random() - 0.5) * 4 : 0;
  
  let temperature = baseTemp + variation + timeEffect + anomaly;
  
  // Clamp to reasonable bounds
  temperature = Math.max(-5, Math.min(20, temperature));
  
  // Round to 1 decimal place
  temperature = Math.round(temperature * 10) / 10;
  
  const status = getTemperatureStatus(temperature, vehicleId);
  
  return {
    vehicleId,
    timestamp: timestamp.toISOString(),
    temperature,
    humidity: 60 + Math.floor(Math.random() * 20), // 60-80%
    batteryLevel: 75 + Math.floor(Math.random() * 25), // 75-100%
    status,
    gpsLocation: {
      lat: -17.8252 + (Math.random() - 0.5) * 0.1,
      lng: 31.0335 + (Math.random() - 0.5) * 0.1,
    },
  };
};

/**
 * Generate historical temperature data
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {number} intervalMinutes - Interval between readings in minutes
 * @returns {Array} - Array of temperature readings
 */
export const generateHistoricalData = (vehicleId, startDate, endDate, intervalMinutes = 30) => {
  const readings = [];
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  let baseTemp = (thresholds.optimal_min + thresholds.optimal_max) / 2;
  
  let currentTime = new Date(startDate);
  const now = new Date();
  
  // Check if the end date is today
  const isToday = endDate.toDateString() === now.toDateString();
  
  // If it's today, limit endDate to current time
  let effectiveEndDate = new Date(endDate);
  if (isToday) {
    effectiveEndDate = new Date(now);
  }
  
  // Add gradual drift over time
  let drift = 0;
  
  while (currentTime <= effectiveEndDate) {
    // Gradually change base temperature to simulate real conditions
    drift += (Math.random() - 0.5) * 0.1;
    drift = Math.max(-1.5, Math.min(1.5, drift)); // Limit drift
    
    const reading = generateTemperatureReading(vehicleId, baseTemp + drift, new Date(currentTime));
    readings.push(reading);
    
    // Move to next interval (30 minutes)
    currentTime = new Date(currentTime.getTime() + intervalMinutes * 60 * 1000);
  }
  
  return readings;
};

/**
 * Generate sparkline data (last 6 hours)
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @returns {Array} - Array of temperature values
 */
export const generateSparklineData = (vehicleId) => {
  const now = new Date();
  const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
  const readings = generateHistoricalData(vehicleId, sixHoursAgo, now, 30);
  
  return readings.map(r => ({
    timestamp: r.timestamp,
    temperature: r.temperature,
  }));
};

/**
 * Simulate sensor reading update
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @param {number} previousTemp - Previous temperature reading
 * @returns {object} - New temperature reading
 */
export const simulateUpdate = (vehicleId, previousTemp = null) => {
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  const baseTemp = previousTemp !== null 
    ? previousTemp 
    : (thresholds.optimal_min + thresholds.optimal_max) / 2;
  
  return generateTemperatureReading(vehicleId, baseTemp);
};

/**
 * Initialize all vehicles with current readings
 * @returns {Array} - Array of current readings for all vehicles
 */
export const initializeVehicleReadings = () => {
  return vehiclesData.vehicles.map(vehicle => {
    const thresholds = thresholdsData.customThresholds[vehicle.id] || thresholdsData.default;
    const baseTemp = (thresholds.optimal_min + thresholds.optimal_max) / 2;
    
    return generateTemperatureReading(vehicle.id, baseTemp);
  });
};

/**
 * Generate mock alert
 * @param {string} vehicleId - Vehicle/Cold room ID
 * @param {number} temperature - Temperature that triggered alert
 * @returns {object} - Alert object
 */
export const generateAlert = (vehicleId, temperature) => {
  const thresholds = thresholdsData.customThresholds[vehicleId] || thresholdsData.default;
  
  let type = 'HIGH_TEMP';
  let severity = 'warning';
  let threshold = thresholds.max_warning;
  
  if (temperature > thresholds.max_critical) {
    type = 'HIGH_TEMP';
    severity = 'critical';
    threshold = thresholds.max_critical;
  } else if (temperature < thresholds.min_critical) {
    type = 'LOW_TEMP';
    severity = 'critical';
    threshold = thresholds.min_critical;
  } else if (temperature < thresholds.min_warning) {
    type = 'LOW_TEMP';
    severity = 'warning';
    threshold = thresholds.min_warning;
  }
  
  return {
    id: `ALT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    vehicleId,
    type,
    severity,
    temperature,
    threshold,
    timestamp: new Date().toISOString(),
    duration: 0,
    acknowledged: false,
    acknowledgedBy: null,
    acknowledgedAt: null,
    resolved: false,
    resolvedAt: null,
    notes: '',
  };
};

